import React, { useContext, useEffect, useState } from "react";
import {
  DataGrid,
  Column,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Scrolling,
  SearchPanel,
  Paging,
  Selection,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import { GetAllSitesInfoDTO, GetSiteHistoryDTO, ServiceMessage } from "../../../api-client-nswag/taxportal-client";
import type { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { dataGridConfig } from "../../../constants";
import {
  calculateGridHeight,
  convertUTCTimeToLocal,
} from "../../../util/Utils";
import { useRecoilState } from "recoil";
import { atom } from "recoil";
import { createSitesClientApi, taxPortalClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { OidcIdentityContext } from "@rsmus/react-auth/dist/OidcIdentity";

interface SiteCommentItem {
  created: string | null;
  createdBy: string | null;
  lastModified: string | null;
  lastModifiedBy: string | null;
  hqSiteId: number;
  siteComment: string | null;
  userName: string | null;
}

interface siteComment {
  pending: boolean;
  data: SiteCommentItem[];
}
interface siteCommentTypes {
  selectedRowData: {
    name: string;
    hqSiteId: number;
  };
  commentSource: "UpgradeSite" | "CreateandSetupSites";
}

interface WrappedResponse {
  statusCode: number;
  message: string;
  data: SiteCommentItem[];
}

const unwrapComments = (response: unknown): SiteCommentItem[] => {
  if (Array.isArray(response)) return response;
  return (response as WrappedResponse)?.data ?? [];
};

export const SiteComments = (props: siteCommentTypes) => {
  const [data, setData] = useState<siteComment>({ pending: true, data: [] });
  const [showTextBox, setShowTextBox] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const { user } = useContext(OidcIdentityContext);
  

  const [commentText, setCommentText] = useState("");
  useEffect(() => {
    try {
      const fetchData = async () => {
        setData({ pending: true, data: [] });
        const response = await createSitesClientApi.taxPortal_GetSiteComments(
          props.selectedRowData.hqSiteId,
          undefined,
        );
        const comments = unwrapComments(response);
        setData({ pending: false, data: comments });
      };
      fetchData();
    } catch (error) {}
  }, [props.selectedRowData.name, props.selectedRowData.hqSiteId]);
  const cellRender = (e: any) => {
    return <div>{props.selectedRowData.name}</div>;
  };

  const datetime = (e: ColumnCellTemplateData) => {
    const created = (e.data as SiteCommentItem).created;
    if (created === null) {
      return null;
    } else {
    return (
      <div style={{ display: "flex" }}>
        <div>
            <div>{convertUTCTimeToLocal(e.data.created)}</div>
        </div>
      </div>
    ); 
    }
  };

  const onClickAddComments = () => {
    setShowTextBox(true);
  };

  const onSubmitComment = async () => {
    if (!commentText.trim()) return;
    await createSitesClientApi.taxPortal_AddSiteComments(null, {
      siteId: props.selectedRowData.hqSiteId,
      siteComments: commentText,
      userName: user?.profile.preferred_username,
      siteCommentSource: props.commentSource,
    });
    setCommentText("");
     setShowTextBox(false);

    // Refresh grid data
    const response = await createSitesClientApi.taxPortal_GetSiteComments(
      props.selectedRowData.hqSiteId,
      undefined,
    );
    const comments = unwrapComments(response);
    setData({ pending: false, data: comments });
  };

  return (
    <div>
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "8px", marginBottom: "10px" }}>
        {!showCommentBox ? (
          <Button
            variant="outlined"
            sx={{ height: 35 }}
            onClick={() => setShowCommentBox(true)}
          >
            Add Comment
          </Button>
        ) : (
          <>
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Enter your comment"
              style={{ flexGrow: 1, height: 35, padding: "0 10px" }}
            />
            <Button
              variant="contained"
              sx={{ height: 35 }}
               onClick={() => {
              onClickAddComments();
              onSubmitComment();
            }}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              sx={{ height: 35 }}
              onClick={() => {
                setShowCommentBox(false);
                setCommentText("");
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>

      {data?.data && (
        <DataGrid
          height={calculateGridHeight(270)}
          {...dataGridConfig}
          dataSource={data.data}
          noDataText={data?.pending ? "Loading..." : "No data"}
          allowColumnResizing={true}
        >
          <Scrolling mode="standard" />
          <LoadPanel enabled={false} />
          <SearchPanel visible />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Paging enabled={true} defaultPageSize={20} />
          <Scrolling columnRenderingMode="virtual" />
      <Column width="10px" alignment="left" dataField="hqSiteId" caption={"Site ID"} />
          <Column
            width="170px"
            alignment="left"
            dataField="hqClientName"
            cellRender={cellRender}
            caption={"Site Name"}
          />
          <Column dataField="siteComment" caption={"Comments"} />
          <Column width="170px" dataField="createdBy" caption={"Created by"} />
          <Column
          width="170px"
          caption="Date & Time"
          dataField="created"
          dataType="string"
          calculateCellValue={(rowData:any) => {
            return rowData.created
              ? convertUTCTimeToLocal(rowData.created)
              : "";
          }}
        />
        </DataGrid>
      )}
    </div>
  );
};
